import mapboxgl from 'mapbox-gl'
import { FC, useEffect, useRef } from 'react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'

import { Colors } from 'src/components/theme'
import { EMPTY_MAP_DATA_SOURCE } from 'src/constants/mapbox'
import { useMap } from 'src/hooks/use-mapbox-map'
import { useMapboxHelper } from 'src/utils/mapbox-main-map'
import { PropertyTooltip } from 'src/v2/components/activity-heatmap-map/property-tooltip'
import { TransactionType } from 'src/v2/views/owner-reveal/insights/owner-summary/activity-heatmap/types'
import * as atoms from 'src/v2/state/owner-reveal/activity-heatmap-map'
import { useCachedAtomValue } from 'src/utils/jotai'
import { AMParcelOverview } from 'src/common-services'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'

const STATE_PROPERTIES_SOURCE_ID = 'state-properties-source'
const STATE_PROPERTIES_LAYER_ID = 'states-properties-layer'

type PropertiesLayerProps = {
  entityId: string
  stateCode?: string
}
export const PropertiesLayer: FC<PropertiesLayerProps> = ({ entityId, stateCode }) => {
  const map = useMap()
  const hoveredPointId = useRef<string | undefined>()
  const { useSource, useLayer, setSourceData } = useMapboxHelper(map)
  const features = useCachedAtomValue(atoms.parcelsFeaturesByStateCode({ stateCode, entityId }), [])
  const { selectOwner } = useMapExplorerNavigation()

  const popup = useRef(
    new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'left',
      offset: [12, 24],
    })
  )
  const popupNode = document.createElement('div')
  const root = createRoot(popupNode)

  const renderPropertyTooltip = (
    property: AMParcelOverview,
    props: {
      onMouseEnter: () => void
      onMouseLeave: () => void
    }
  ) => {
    const parcel = {
      ...property,
      owners: JSON.parse(property.owners as unknown as string),
    }

    return (
      <PropertyTooltip
        transactionType={TransactionType.Purchase}
        price={0}
        date="05/12/2024"
        onClickItem={({ id }) => selectOwner(id)}
        parcel={parcel}
        {...props}
      />
    )
  }

  useSource(STATE_PROPERTIES_SOURCE_ID, {
    type: 'geojson',
    data: EMPTY_MAP_DATA_SOURCE,
    promoteId: 'id',
  })

  useLayer({
    id: STATE_PROPERTIES_LAYER_ID,
    source: STATE_PROPERTIES_SOURCE_ID,
    type: 'circle',
    paint: {
      'circle-color': Colors.primaryRed700,
      'circle-stroke-color': Colors.primaryRed800,
      'circle-stroke-width': 2,
      'circle-radius': 6,
    },
  })

  useEffect(() => {
    let mouseLeaveTimeout: NodeJS.Timeout | null = null

    const onMouseEnter = (ev: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (ev.features?.length) {
        const feature = ev.features[0]
        const id = feature.properties.id

        if (hoveredPointId.current !== id) {
          if (hoveredPointId.current) {
            map.setFeatureState({ source: STATE_PROPERTIES_SOURCE_ID, id: hoveredPointId.current }, { hover: false })
          }

          hoveredPointId.current = id
          map.setFeatureState({ source: STATE_PROPERTIES_SOURCE_ID, id: hoveredPointId.current }, { hover: true })
          flushSync(() =>
            root.render(
              renderPropertyTooltip(feature.properties, { onMouseEnter: clearMouseLeaveTimeout, onMouseLeave })
            )
          )
          popup.current?.setLngLat(ev.lngLat).setDOMContent(popupNode).addTo(map)
        }
      }
    }

    const onMouseLeave = () => setMouseLeaveTimeout()

    const setMouseLeaveTimeout = () => {
      mouseLeaveTimeout = setTimeout(() => {
        if (hoveredPointId.current) {
          map.setFeatureState({ source: STATE_PROPERTIES_SOURCE_ID, id: hoveredPointId.current }, { hover: false })
        }
        popup.current?.remove()
        hoveredPointId.current = undefined
      }, 150)
    }

    const clearMouseLeaveTimeout = () => {
      if (mouseLeaveTimeout !== null) {
        clearTimeout(mouseLeaveTimeout)
        mouseLeaveTimeout = null
      }
    }

    map.on('mouseenter', STATE_PROPERTIES_LAYER_ID, onMouseEnter)
    map.on('mouseleave', STATE_PROPERTIES_LAYER_ID, onMouseLeave)

    return () => {
      map.off('mouseenter', STATE_PROPERTIES_LAYER_ID, onMouseEnter)
      map.off('mouseleave', STATE_PROPERTIES_LAYER_ID, onMouseLeave)
    }
  }, [])

  useEffect(() => {
    if (features.length > 0) {
      setSourceData(STATE_PROPERTIES_SOURCE_ID, {
        type: 'FeatureCollection',
        features: features,
      })
    } else {
      setSourceData(STATE_PROPERTIES_SOURCE_ID, EMPTY_MAP_DATA_SOURCE)
    }
  }, [features])

  return null
}
